import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { triggerScraper, getScraperStatus } from "../services/api";

export default function ManualTriggerPanel() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("idle");
  const [lastRun, setLastRun] = useState(null);
  const [jobsFound, setJobsFound] = useState(null);

  useEffect(() => {
    getScraperStatus().then((s) => {
      setStatus(s.status);
      setLastRun(s.lastRun);
      setJobsFound(s.jobsFound);
    });
  }, []);

  const handleRun = async () => {
    setStatus("running");
    setJobsFound(null);
    try {
      await triggerScraper();
      setStatus("triggered");
      setLastRun(new Date().toISOString());
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
        queryClient.invalidateQueries({ queryKey: ["stats"] });
        setStatus("done");
      }, 120_000);
    } catch {
      setStatus("idle");
    }
  };

  const statusBadge = {
    running:   "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    triggered: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
    done:      "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
    idle:      "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 px-4 py-3 space-y-2">
      <div className="flex items-center gap-3">
        <button
          onClick={handleRun}
          disabled={status === "running" || status === "triggered"}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation"
        >
          {status === "running" ? (
            <>
              <svg className="animate-spin h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Triggering…
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Run Scraper
            </>
          )}
        </button>

        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${statusBadge[status] ?? statusBadge.idle}`}>
          {status === "triggered" ? "running" : status}
        </span>

        {status !== "triggered" && lastRun && (
          <span className="hidden sm:block text-xs text-gray-400 dark:text-gray-500 ml-auto">
            Last run: <span className="text-gray-600 dark:text-gray-300 font-medium">{new Date(lastRun).toLocaleString()}</span>
          </span>
        )}
      </div>

      {status === "triggered" && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Scanning 13 keywords in background — feed refreshes in ~2 min
        </p>
      )}
      {status !== "triggered" && lastRun && (
        <p className="block sm:hidden text-xs text-gray-400 dark:text-gray-500">
          Last: {new Date(lastRun).toLocaleString()}
        </p>
      )}
    </div>
  );
}
